<template>
	<div class="advanced-container">
		<ModalHeader
			:title="title"
			:button-text="getString('save')"
			@click-button="saveClicked" />
		<div class="advanced-options">
			<AppList :items="items" >
				<template v-slot:dropdown-vat="{item}">
					<AppList :items="vatOptions">
						<template v-slot:dropdown-vat-nature="{item}">
							<AppList :items="item.children">
								<template #dropdown-vat-select="{item}">
									<div class="row centered space-between ">
										<p class="list-item-title">{{item.title}}</p>
										<div class="left-portion">
											<p class="mr-10 mt-5 color-txt-2">{{item.selectedText}}</p>
											<AppButton class="right-item" small outline :text="item.buttonTitle"
													   :disabled="item.disabled" @click="item.callbackButton" />
										</div>
									</div>
								</template>
							</AppList>
						</template>
					</AppList>
				</template>
			</AppList>
		</div>

		<AppBottomSheet :active="showVatNatureBottomSheet" @toggling-popup="togglePopup" :force-width="bottomSheetWidth">
			<VatNatureBottomSheet :categoryTempId="categoryTempId" @apply="updateNature"/>
		</AppBottomSheet>
		<AppBottomSheet :active="showVatNatureRegRefBottomSheet" @toggling-popup="togglePopupRegRef" :force-width="bottomSheetWidth">
			<VatNatureRegulatoryReferenceBottomSheet :nature="nature ?? ''" :reg-ref-index="regRefIndex ?? 0" @apply="updateRegRef"/>
		</AppBottomSheet>

	</div>
</template>

<script>
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppList from "@/shared/components/AppList.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useMenuStore, useUserStore, usePrintersStore, useEpsonStore} from "@/console-new/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import VatNatureBottomSheet from "@/console-new/components/config/menu/VatNatureBottomSheet.vue";
import VatNatureRegulatoryReferenceBottomSheet from "@/console-new/components/config/menu/VatNatureRegulatoryReferenceBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let printersStore = usePrintersStore();
		let userStore = useUserStore();
		let epsonStore = useEpsonStore();
		return {getString: languageStore.getString, menuStore, userStore, printersStore, epsonStore,
			showNotification: useNotificationStore().showNotification};
	},
	name: "AdvancedCategoryEditor",
	mixins: [WindowWidth],
	emits: ["save-clicked"],
	components: {AppButton, AppSelect, AppList, ModalHeader, AppNumberInput, AppBottomSheet, VatNatureBottomSheet, VatNatureRegulatoryReferenceBottomSheet},
	props: {
		categoryTempId: {
			type: String,
			required: true
		}
	},
	data() {
		return {
			printersOptions: [],
			variationsOnly: false,
			showVatNatureBottomSheet: false,
			showVatNatureRegRefBottomSheet: false,
			nature: null,
			regRefIndex: null,
			reg: null,
		}
	},
	computed: {
		title() {
			return this.getString("title_category_advanced", [this.category.name]);
		},
		category() {
			return this.menuStore.modMenu.categories[this.categoryTempId];
		},
		vats() {
			let vats = this.epsonStore.countryVats["IT"];
			if(this.userStore.currBusiness && this.epsonStore.countryVats.hasOwnProperty(this.userStore.currBusiness.country)) {
				vats = this.epsonStore.countryVats[this.userStore.currBusiness.country];
			}

			return vats;
		},
		items() {
			let fixedOptions = [];
			if (this.userStore.currPlans.counter || this.userStore.currPlans.order_pay) {
				fixedOptions.push({
					title: this.getString('vat_value'),
					switch: true,
					active: this.category.vat !== null,
					dropdownSlot: "dropdown-vat",
					callback: (v) => {
						if(v) {
							this.category.vat = this.vats[0];
						} else {
							this.category.vat = null;
							this.category.natura_iva = null;
						}
					},
				});
			}
			if (this.userStore.currPlans.delivery) {
				fixedOptions.push({
					title: this.userStore.deliveryWord,
					switch: true,
					active: this.category.active_delivery,
					callback: (v) => this.category.active_delivery = v
				});
			}
			if (this.userStore.currPlans.takeaway) {
				fixedOptions.push({
					title: this.userStore.takeawayWord,
					switch: true,
					active: this.category.active_takeaway,
					callback: (v) => this.category.active_takeaway = v
				});
			}
			fixedOptions.push({
				title: this.getString('use_as_variations_list_title'),
				explain: this.getString('use_as_variations_list_explain', [this.userStore.menuWord]),
				switch: true,
				active: !!this.variationsOnly,
				callback: v => {
					this.variationsOnly = !!v;
					if(this.variationsOnly) {
						this.category.additional_costs_shortcuts = false;
					}
				}
			});
			fixedOptions.push({
				title: this.getString('shortcuts_category_title'),
				explain: this.getString('shortcuts_category_explain', [this.userStore.menuWord]),
				switch: true,
				active: !!this.category.additional_costs_shortcuts,
				callback: v => {
					this.category.additional_costs_shortcuts = !!v;
					if(this.category.additional_costs_shortcuts) {
						this.variationsOnly = false;
					}
				}
			});
			let self = this;
			if(this.printersStore.businessPrinters != null) {
				for (let i in this.printersStore.businessPrinters) {
					fixedOptions.push({
						title: this.getString('printer_name', [this.printersStore.businessPrinters[i].name]),
						switch: true,
						active: (this.category.menu_category_id && !this.printersStore.businessPrinters[i].categoriesExcl.find(c => c === self.category.menu_category_id)),
						callback: (v) => this.printerSwitched(v, this.printersStore.businessPrinters[i])
					});
				}
			}
			fixedOptions.push({
				title: this.getString('print_priority'),
				numberInput: true,
				value: this.category.print_priority,
				min: 1,
				max: 1000,
				callbackNumber: (v) => this.category.print_priority = v,
			});
			if ( fixedOptions.length === 0 ) { fixedOptions.push({ title: this.getString('empty_rows') }); }
			return fixedOptions;
		},
		vatOptions() {
			let index = this.vats.findIndex(v => parseFloat(v) === parseFloat(this.category.vat));

			if(index === -1) {
				index = 0;
			}

			return [
				{
					title: this.getString('vat_value'),
					selectValues: this.vats,
					selectedIndex: index,
					active: parseFloat(this.category.vat) === 0,
					callbackSelect: this.vatSwitched,
					dropdownSlot: 'dropdown-vat-nature',
					children: [
						{
							title: this.getString('vat_nature'),
							customSlot: 'dropdown-vat-select',
							buttonTitle: this.getString('vat_nature_btn'),
							selectedText: this.nature,
							disabled: false,
							callbackButton: () => this.showVatNatureBottomSheet = true
						},
						{
							title: this.getString('vat_nature_regulations'),
							customSlot: 'dropdown-vat-select',
							buttonTitle: this.getString('vat_nature_regulations_btn'),
							selectedText: this.reg,
							disabled: this.nature === null,
							callbackButton: () => this.showVatNatureRegRefBottomSheet = true
						}
					]
				}
			]
		},
		vat() {
			return this.category.vat !== null ? Number(this.category.vat) : 0;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		}
	},
	methods: {
		vatSwitched(index) {
			this.category.vat = this.vats[index];
		},
		printerSwitched(active, printer) {
			if (active) {
				printer.categoriesExcl = printer.categoriesExcl.filter(c => c !== this.category.menu_category_id);
			} else {
				printer.categoriesExcl.push(this.category.menu_category_id);
			}
		},
		saveClicked() {
			if(this.category.menu_category_id) {
				this.printersStore.updateCategoryPrinters(this.category.menu_category_id);
			}
			if(parseInt(this.category.vat) === 0) {
				if(this.nature !== null && this.regRefIndex !== null) {
					this.category.natura_iva = this.nature+'.'+this.regRefIndex;
				}
				else {
					this.showNotification('vat-nature-not-set')
					return;
				}
			}
			this.$emit("save-clicked", this.variationsOnly);
		},
		togglePopup(active) {
			if (!active) {
				this.showVatNatureBottomSheet = active;
			}
		},
		togglePopupRegRef(active) {
			if (!active) {
				this.showVatNatureRegRefBottomSheet = active;
			}
		},
		updateNature(nature) {
			this.nature = nature;
			this.regRefIndex = null;
			this.reg = null;
			this.togglePopup(false);
		},
		updateRegRef(regRefIndex, reg) {
			this.regRefIndex = regRefIndex;
			this.reg = reg;
			this.togglePopupRegRef(false);
		}
	},
	mounted() {

		this.printersStore.getBusinessPrinters();
		this.variationsOnly = !!this.category.variations_only;

		if(this.category.natura_iva !== null) {
			let parsedNature = this.epsonStore.parseVatNature(
				this.userStore.currBusiness.country,
				this.category.natura_iva
			);

			this.nature = parsedNature.nature;
			this.regRefIndex = parsedNature.regRefIndex;
			this.reg = parsedNature.reg;
		}

		if(this.category.print_priority !== null) {
			this.printPriority = this.category.print_priority;
		}
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.printersStore.getBusinessPrinters();
			}
		}
	},
}
</script>

<style scoped>
.advanced-container {
	.left-portion {
		display: flex;
		flex-direction: row;
	}
}
</style>
